import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useVblData, useFilterOptions } from '@/hooks/useFailures';
import { useVblAnalysis } from '@/hooks/useVblAnalysis';
import { useWorkspaceContext } from '@/hooks/useWorkspaceContext';
import { useLayout } from '@/contexts';
import { VblRecord, VBL_ANALYSIS_METHOD } from '@/types/vbl';
import { FailureFilters, DEFAULT_FAILURE_FILTERS } from '@/types/filters';
import { Workspace } from '@/types/workspace';
import { WorkspaceContext } from '@/types/workspaceContext';
import { VblWeibullPanel, DualVblPlotView } from '@/components/vbl';
import { SidebarController } from '@/components/analysis/SidebarController';
import { ColorByOption, MarkerByOption, SizeByOption } from '@/components/analysis/PlotCustomization';
import { SaveWorkspaceModal, LoadWorkspaceModal } from '@/components/workspaces';
import { PageToolbar, ToolbarIcons, WaveLoading, StatsCard, TableCard, ResponsiveGrid, ParameterGroupCard } from '@/components/ui';
import ColumnHeader from '@/components/table/ColumnHeader';
import { useKeyboardShortcuts } from '@/hooks/useKeyboardShortcuts';
import { usePermissions } from '@/hooks/usePermissions';
import { MinimalWorkspace } from '@/utils/workspacePermissions';
import WorkspaceErrorBoundary from '@/components/error/WorkspaceErrorBoundary';
import { ChevronDownIcon, ChartBarSquareIcon } from '@heroicons/react/24/outline';
import { externalLinks } from '@/config/externalLinks';

/**
 * VBL Analysis Page - Main VBL analysis interface with workspace management
 * EXACT MIRROR of Analysis.tsx but for VBL data (degradation time instead of operation time)
 *
 * WORKSPACE BEHAVIOR:
 * - Changes are tracked but NOT auto-saved
 * - Users must explicitly save to persist modifications
 * - "New Workspace" creates fresh state with no filters
 * - Load options: "Snapshot" (exact historical) vs "Recalculate" (fresh data)
 * - All data points included in analysis (not just paginated table view)
 */
const VblAnalysis: React.FC = () => {
  const navigate = useNavigate();
  const { getContentAreaStyle, shouldShowDualPlots } = useLayout();
  const [filters, setFilters] = useState<FailureFilters>(DEFAULT_FAILURE_FILTERS);
  const [showConfidenceIntervals, setShowConfidenceIntervals] = useState(true);
  const [confidenceLevel, setConfidenceLevel] = useState(95);
  const [showCensoredData, setShowCensoredData] = useState(true);


  // Workspace modal states
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showLoadModal, setShowLoadModal] = useState(false);

  // Permissions
  const permissions = usePermissions();

  // Workspace context management (single source of truth)
  const [workspaceContext, workspaceContextActions] = useWorkspaceContext();

  // UI-level pagination state
  const [currentTablePage, setCurrentTablePage] = useState(1);
  const tablePageSize = 20;

  // Expandable rows state
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  // Plot type state for VBL Analysis
  const [plotType, setPlotType] = useState<'probability' | 'cdf'>('probability');
  const [showDualPlots, setShowDualPlots] = useState(false);

  // Plot customization state
  const [colorBy, setColorBy] = useState<ColorByOption>('none');
  const [markerBy, setMarkerBy] = useState<MarkerByOption>('none');
  const [sizeBy, setSizeBy] = useState<SizeByOption>('uniform');
  const [showLegend, setShowLegend] = useState(false);

  // Check if we should show dual plots
  useEffect(() => {
    const checkDualPlots = () => {
      setShowDualPlots(shouldShowDualPlots());
    };

    checkDualPlots();
    window.addEventListener('resize', checkDualPlots);
    return () => window.removeEventListener('resize', checkDualPlots);
  }, [shouldShowDualPlots]);

  // Table-only filtering and sorting state (doesn't affect analysis)
  const [tableFilters, setTableFilters] = useState({
    sites: [] as string[],
    turbineModels: [] as string[],
    componentsMajor: [] as string[],
    componentsMinor: [] as string[],
    failureModes: [] as string[],
    outcomeName: [] as string[],
    degradationTimeMin: undefined as number | undefined,
    degradationTimeMax: undefined as number | undefined,
    dateFrom: undefined as string | undefined,
    dateTo: undefined as string | undefined,
  });

  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: 'asc' | 'desc';
  } | null>(null);

  // Check for pending workspace load from Dashboard navigation
  useEffect(() => {
    const pendingLoad = sessionStorage.getItem('pendingVblWorkspaceLoad');
    if (pendingLoad) {
      try {
        const { workspace, workspaceContext: loadedContext, timestamp } = JSON.parse(pendingLoad);

        // Check if the load is recent (within 10 seconds) to avoid stale loads
        if (Date.now() - timestamp < 10000) {

          // Apply the loaded workspace to current state
          setFilters(workspace.configuration.filters);
          setShowConfidenceIntervals(workspace.configuration.analysisParams.showConfidenceIntervals);
          setConfidenceLevel(workspace.configuration.analysisParams.confidenceLevel);
          setShowCensoredData(workspace.configuration.analysisParams.showCensoredData);


          // Update workspace context with loaded context
          workspaceContextActions.setWorkspaceContext(loadedContext);
        }

        // Clean up the pending load
        sessionStorage.removeItem('pendingVblWorkspaceLoad');
      } catch (error) {
        console.error('Failed to load pending VBL workspace:', error);
        sessionStorage.removeItem('pendingVblWorkspaceLoad');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only once when component mounts

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentTablePage(1);
  }, [filters]);

  // Query for complete VBL dataset (not paginated)
  const completeFilters = {
    ...filters,
    excludedCaseIds: [], // Get all data for complete dataset
    size: 10000, // Get all available data
    page: 1,
  };

  const {
    data: vblData,
    isLoading: isLoadingVbl,
    error: vblError,
  } = useVblData(completeFilters);

  const { data: apiFilterOptions, isLoading: isLoadingOptions } = useFilterOptions();

  const {
    analysis: vblAnalysis,
    isLoading: isVblLoading,
    hasEnoughData,
    sampleSize,
    metadata,
    diversityMetrics,
  } = useVblAnalysis(
    {
      ...filters,
      size: 10000, // Ensure VBL analysis uses ALL data
      page: 1,
    },
    confidenceLevel / 100
  );

  // Complete VBL dataset (for workspace saving and analysis)
  const completeVblRecords = useMemo(() => {
    return vblData?.vbls || [];
  }, [vblData?.vbls]);

  // Enhanced VBL analysis result with forecast constraints
  const enhancedVblAnalysis = useMemo(() => {
    if (!vblAnalysis) return null;

    return vblAnalysis;
  }, [vblAnalysis]);

  // Apply table-only filtering and sorting (doesn't affect analysis)
  const filteredAndSortedVblRecords = useMemo(() => {
    let filtered = [...completeVblRecords];

    // Apply table filters (for display only)
    if (tableFilters.sites.length > 0) {
      filtered = filtered.filter((f) => tableFilters.sites.includes(f.site_name));
    }
    if (tableFilters.turbineModels.length > 0) {
      filtered = filtered.filter((f) => tableFilters.turbineModels.includes(f.turbine_name));
    }
    if (tableFilters.componentsMajor.length > 0) {
      filtered = filtered.filter((f) => tableFilters.componentsMajor.includes(f.major_component_name));
    }
    if (tableFilters.componentsMinor.length > 0) {
      filtered = filtered.filter(
        (f) => f.component_name && tableFilters.componentsMinor.includes(f.component_name)
      );
    }
    if (tableFilters.failureModes.length > 0) {
      filtered = filtered.filter((f) => tableFilters.failureModes.includes(f.failure_mode));
    }
    if (tableFilters.outcomeName.length > 0) {
      filtered = filtered.filter((f) => tableFilters.outcomeName.includes(f.outcome_name));
    }
    if (tableFilters.degradationTimeMin !== undefined) {
      filtered = filtered.filter((f) => f.degradation_time >= tableFilters.degradationTimeMin!);
    }
    if (tableFilters.degradationTimeMax !== undefined) {
      filtered = filtered.filter((f) => f.degradation_time <= tableFilters.degradationTimeMax!);
    }
    if (tableFilters.dateFrom) {
      filtered = filtered.filter(
        (f) => new Date(f.detected_date) >= new Date(tableFilters.dateFrom!)
      );
    }
    if (tableFilters.dateTo) {
      filtered = filtered.filter((f) => new Date(f.detected_date) <= new Date(tableFilters.dateTo!));
    }

    // Apply sorting
    if (sortConfig) {
      filtered.sort((a, b) => {
        let aValue: string | number | Date;
        let bValue: string | number | Date;

        switch (sortConfig.key) {
          case 'caseId':
            // Natural/alphanumeric sorting
            return a.case_id.toString().localeCompare(b.case_id.toString(), undefined, {
              numeric: true,
              sensitivity: 'base'
            }) * (sortConfig.direction === 'asc' ? 1 : -1);
          case 'turbine':
            aValue = a.turbine_name;
            bValue = b.turbine_name;
            break;
          case 'site':
            aValue = a.site_name;
            bValue = b.site_name;
            break;
          case 'componentMajor':
            aValue = a.major_component_name;
            bValue = b.major_component_name;
            break;
          case 'componentMinor':
            aValue = a.component_name || '';
            bValue = b.component_name || '';
            break;
          case 'failureMode':
            aValue = a.failure_mode;
            bValue = b.failure_mode;
            break;
          case 'type':
            aValue = a.outcome_name;
            bValue = b.outcome_name;
            break;
          case 'degradationTime':
            aValue = a.degradation_time;
            bValue = b.degradation_time;
            break;
          case 'detectionDate':
            aValue = new Date(a.detected_date);
            bValue = new Date(b.detected_date);
            break;
          case 'downDate':
            aValue = a.down_date ? new Date(a.down_date) : new Date(0);
            bValue = b.down_date ? new Date(b.down_date) : new Date(0);
            break;
          default:
            return 0;
        }

        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }

    return filtered;
  }, [completeVblRecords, tableFilters, sortConfig]);

  // Paginated VBL records for table display (shows filtered and sorted cases)
  const paginatedVblRecords = useMemo(() => {
    const start = (currentTablePage - 1) * tablePageSize;
    return filteredAndSortedVblRecords.slice(start, start + tablePageSize);
  }, [filteredAndSortedVblRecords, currentTablePage, tablePageSize]);

  // Table pagination metadata (counts ALL cases)
  const tablePaginationData = useMemo(() => {
    const excludedCaseIds = filters.excludedCaseIds || [];
    const includedCount = filteredAndSortedVblRecords.filter(
      (f) => !excludedCaseIds.includes(f.case_id.toString())
    ).length;

    return {
      totalCount: filteredAndSortedVblRecords.length, // Total filtered cases in table
      includedCount, // Cases included in analysis (not excluded)
      excludedCount: filteredAndSortedVblRecords.length - includedCount, // Cases excluded from analysis
      totalPages: Math.ceil(filteredAndSortedVblRecords.length / tablePageSize),
      hasMore: currentTablePage < Math.ceil(filteredAndSortedVblRecords.length / tablePageSize),
      currentPage: currentTablePage,
      pageSize: tablePageSize,
    };
  }, [filteredAndSortedVblRecords, filters.excludedCaseIds, currentTablePage, tablePageSize]);

  const handleFiltersChange = (newFilters: FailureFilters) => {
    setFilters(newFilters);
    // Mark workspace context as changed
    workspaceContextActions.markWorkspaceAsChanged();
  };

  const handleToggleVblRecord = (vblId: string, caseId: string | number) => {
    const caseIdStr = caseId.toString();
    const currentExcluded = filters.excludedCaseIds || [];
    const isCurrentlyExcluded = currentExcluded.includes(caseIdStr);

    if (isCurrentlyExcluded) {
      // Remove from excluded list (select it)
      handleFiltersChange({
        ...filters,
        excludedCaseIds: currentExcluded.filter((id) => id !== caseIdStr),
      });
    } else {
      // Add to excluded list (deselect it)
      handleFiltersChange({
        ...filters,
        excludedCaseIds: [...currentExcluded, caseIdStr],
      });
    }
  };

  const handleSelectVblRecord = (vblId: string) => {
    const vblRecord = completeVblRecords.find((f) => f.failure_id === vblId);
    if (vblRecord) {
      handleToggleVblRecord(vblId, vblRecord.case_id);
    }
  };

  const handleSelectAll = () => {
    const currentExcluded = filters.excludedCaseIds || [];
    const allCaseIds = Array.from(new Set(filteredAndSortedVblRecords.map((f) => f.case_id.toString())));
    const allExcluded = allCaseIds.every((id) => currentExcluded.includes(id));

    if (allExcluded) {
      // All visible are excluded, so select all (remove from excluded)
      handleFiltersChange({
        ...filters,
        excludedCaseIds: currentExcluded.filter((id) => !allCaseIds.includes(id)),
      });
    } else {
      // Some or none are excluded, so exclude all visible
      handleFiltersChange({
        ...filters,
        excludedCaseIds: Array.from(new Set([...currentExcluded, ...allCaseIds])),
      });
    }
  };

  const handleSelectAllInPage = () => {
    const currentExcluded = filters.excludedCaseIds || [];
    const visibleCaseIds = Array.from(new Set(paginatedVblRecords.map((f) => f.case_id.toString())));
    const allVisibleExcluded = visibleCaseIds.every((id) => currentExcluded.includes(id));

    if (allVisibleExcluded) {
      // All visible are excluded, so select all (remove from excluded)
      handleFiltersChange({
        ...filters,
        excludedCaseIds: currentExcluded.filter((id) => !visibleCaseIds.includes(id)),
      });
    } else {
      // Some or none are excluded, so exclude all visible
      handleFiltersChange({
        ...filters,
        excludedCaseIds: Array.from(new Set([...currentExcluded, ...visibleCaseIds])),
      });
    }
  };


  // Toggle row expansion
  const toggleRowExpansion = (vblId: string) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(vblId)) {
      newExpanded.delete(vblId);
    } else {
      newExpanded.add(vblId);
    }
    setExpandedRows(newExpanded);
  };

  // Expand/Collapse all visible rows
  const toggleAllRows = () => {
    const visibleIds = paginatedVblRecords.map((f) => f.failure_id);
    const allVisibleExpanded = visibleIds.every((id) => expandedRows.has(id));

    if (allVisibleExpanded) {
      // Collapse all visible
      const newExpanded = new Set(expandedRows);
      visibleIds.forEach((id) => newExpanded.delete(id));
      setExpandedRows(newExpanded);
    } else {
      // Expand all visible
      const currentExpanded = Array.from(expandedRows);
      setExpandedRows(new Set([...currentExpanded, ...visibleIds]));
    }
  };

  // Table-only filter handlers (don't affect analysis)
  const handleTableColumnFilter = (column: keyof typeof tableFilters, values: string[]) => {
    setTableFilters((prev) => ({
      ...prev,
      [column]: values,
    }));
    setCurrentTablePage(1); // Reset pagination
  };

  const handleTableRangeFilter = (min: number | undefined, max: number | undefined) => {
    setTableFilters((prev) => ({
      ...prev,
      degradationTimeMin: min,
      degradationTimeMax: max,
    }));
    setCurrentTablePage(1);
  };

  const handleTableDateRangeFilter = (from: string | undefined, to: string | undefined) => {
    setTableFilters((prev) => ({
      ...prev,
      dateFrom: from,
      dateTo: to,
    }));
    setCurrentTablePage(1);
  };

  const handleSort = (key: string) => {
    setSortConfig((prevSort) => {
      if (prevSort?.key === key) {
        // 3-state toggle: asc -> desc -> null -> asc
        if (prevSort.direction === 'asc') {
          return { key, direction: 'desc' };
        } else if (prevSort.direction === 'desc') {
          return null; // No sorting
        }
      }
      // New column or returning from null, default to ascending
      return { key, direction: 'asc' };
    });
  };

  // Handler for analysis configuration changes
  const handleAnalysisConfigChange = (key: string, value: boolean | number) => {
    // Update local state with proper type handling
    if (key === 'showConfidenceIntervals' && typeof value === 'boolean') {
      setShowConfidenceIntervals(value);
    }
    if (key === 'confidenceLevel' && typeof value === 'number') {
      setConfidenceLevel(value);
    }
    if (key === 'showCensoredData' && typeof value === 'boolean') {
      setShowCensoredData(value);
    }

    // Mark workspace context as changed
    workspaceContextActions.markWorkspaceAsChanged();
  };


  // Handle table pagination (UI-level)
  const handleTablePageChange = (page: number) => {
    setCurrentTablePage(page);
  };

  const handleNewWorkspace = () => {
    // Reset to default filters and analysis config
    setFilters(DEFAULT_FAILURE_FILTERS);
    setShowConfidenceIntervals(true);
    setConfidenceLevel(95);
    setShowCensoredData(true);
    setCurrentTablePage(1);
    setExpandedRows(new Set());
    setTableFilters({
      sites: [],
      turbineModels: [],
      componentsMajor: [],
      componentsMinor: [],
      failureModes: [],
      outcomeName: [],
      degradationTimeMin: undefined,
      degradationTimeMax: undefined,
      dateFrom: undefined,
      dateTo: undefined,
    });
    setSortConfig(null);

    // Clear workspace context
    workspaceContextActions.clearWorkspaceContext();
  };

  const handleSaveAnalysis = () => {
    // Open save modal - always saves complete dataset and cached results
    // No user options needed - simplified professional workflow
    setShowSaveModal(true);
  };

  const handleLoadAnalysis = () => {
    // Open load modal
    setShowLoadModal(true);
  };

  const handleLoadWorkspace = async (
    workspace: Workspace,
    loadedWorkspaceContext: WorkspaceContext
  ) => {
    try {
      // Apply the loaded workspace to current state
      setFilters(workspace.configuration.filters);
      setShowConfidenceIntervals(workspace.configuration.analysisParams.showConfidenceIntervals);
      setConfidenceLevel(workspace.configuration.analysisParams.confidenceLevel);
      setShowCensoredData(workspace.configuration.analysisParams.showCensoredData);


      // Update workspace context with loaded context
      workspaceContextActions.setWorkspaceContext(loadedWorkspaceContext);

      // Show success message or notification

      // Note: Changes to loaded workspaces are NOT auto-saved
      // Users must explicitly save to persist modifications
    } catch (error) {
      console.error('Failed to load VBL workspace:', error);
      // Handle error - could show error notification
    }
  };

  const handleSaveSuccess = (workspace: Workspace) => {
    // Update workspace context after successful save
    workspaceContextActions.updateWorkspaceAfterSave(workspace);
    // Could show toast notification here
  };

  // Auto-enable legend when plot customization is used
  const handleColorByChange = (value: ColorByOption) => {
    setColorBy(value);
    if (value !== 'none') {
      setShowLegend(true);
    }
  };

  const handleMarkerByChange = (value: MarkerByOption) => {
    setMarkerBy(value);
    if (value !== 'none') {
      setShowLegend(true);
    }
  };

  const handleSizeByChange = (value: SizeByOption) => {
    setSizeBy(value);
    if (value !== 'uniform') {
      setShowLegend(true);
    }
  };

  const _handleCreateForecast = () => {
    // Navigate to VBL Forecast page
    navigate('/vbl-forecast');
  };

  // Keyboard shortcuts
  useKeyboardShortcuts({
    onSave: handleSaveAnalysis,
    onLoad: handleLoadAnalysis,
    onNew: handleNewWorkspace,
  });

  // Only show full-screen loading on initial page load (filter options)
  if (isLoadingOptions) {
    return (
      <WaveLoading
        fullScreen={true}
        size="xl"
        variant="wave"
        color="bright-blue"
        text="Loading filter options..."
      />
    );
  }

  if (vblError) {
    return (
      <WorkspaceErrorBoundary>
        <div className="min-h-screen flex items-center justify-center">
          <div className="alert alert-error max-w-md">
            <div>
              <h3 className="onyx-subtitle">Error loading VBL data</h3>
              <div className="onyx-caption">Please try refreshing the page</div>
            </div>
          </div>
        </div>
      </WorkspaceErrorBoundary>
    );
  }

  return (
    <WorkspaceErrorBoundary>
      <div className="min-h-screen bg-gray-50 flex flex-col">
        {/* Tool Page Header */}
        <PageToolbar
          title="VBL Analysis"
          icon={ChartBarSquareIcon}
          buttons={[
            {
              key: 'new-workspace',
              label: 'New Workspace',
              icon: ToolbarIcons.NewWorkspace,
              onClick: handleNewWorkspace,
              variant: 'secondary',
              tooltip: 'Start a new workspace (clears current filters and data)'
            },
            {
              key: 'load-workspace',
              label: 'Load Workspace',
              icon: ToolbarIcons.LoadWorkspace,
              onClick: handleLoadAnalysis,
              variant: 'secondary',
              tooltip: 'Load a saved workspace'
            },
            {
              key: 'save-workspace',
              label: workspaceContext.isWorkspaceLoaded ?
                (permissions.canEditWorkspace(workspaceContext.currentWorkspace as MinimalWorkspace) ? 'Update Workspace' : 'Save as New') :
                'Save Workspace',
              icon: ToolbarIcons.SaveWorkspace,
              onClick: handleSaveAnalysis,
              variant: 'primary',
              tooltip: workspaceContext.isWorkspaceLoaded ?
                (permissions.canEditWorkspace(workspaceContext.currentWorkspace as MinimalWorkspace) ?
                  'Update current workspace' :
                  'You cannot edit this workspace - will save as new') :
                'Save current analysis as workspace'
            }
          ]}
          statusInfo={{
            workspaceName: workspaceContext.currentWorkspace.name,
            hasUnsavedChanges: workspaceContext.currentWorkspace.hasUnsavedChanges
          }}
        />

        {/* Main Content Layout */}
        <div className="flex flex-1 overflow-hidden">
          {/* Stackable Sidebars */}
          <SidebarController
            // Filter props
            filters={filters}
            onFiltersChange={handleFiltersChange}
            filterOptions={
              apiFilterOptions || {
                sites: [],
                turbineModels: [],
                componentsMajor: [],
                componentsMinor: [],
                failureModes: [],
                outcomeName: [],
                excludedCaseIds: [],
              }
            }
            isLoading={false}
            // Analysis settings props
            plotType={plotType}
            onPlotTypeChange={setPlotType}
            showConfidenceIntervals={showConfidenceIntervals}
            confidenceLevel={confidenceLevel}
            onToggleConfidenceIntervals={(value) =>
              handleAnalysisConfigChange('showConfidenceIntervals', value)
            }
            onConfidenceLevelChange={(value) =>
              handleAnalysisConfigChange('confidenceLevel', value)
            }
            showCensoredData={showCensoredData}
            onToggleCensoredData={(value) =>
              handleAnalysisConfigChange('showCensoredData', value)
            }
            // VBL doesn't use method selection - omit selectedMethod and onMethodChange
            // Plot customization props
            colorBy={colorBy}
            onColorByChange={handleColorByChange}
            markerBy={markerBy}
            onMarkerByChange={handleMarkerByChange}
            sizeBy={sizeBy}
            onSizeByChange={handleSizeByChange}
            showLegend={showLegend}
            onShowLegendChange={setShowLegend}
            // VBL-specific props
            isVblAnalysis={true}
            vblAnalysis={vblAnalysis}
          />

          {/* Main Content Area */}
          <div className="flex-1 overflow-y-auto min-w-0">
            <div style={getContentAreaStyle()}>
              {/* Summary Statistics - Using ResponsiveGrid */}
              <ResponsiveGrid minCardWidth={250} gap="md" className="mb-6">
                <StatsCard
                  title="Total VBL Records"
                  value={metadata?.failureCount || 0}
                  description="Detection-to-failure records"
                  color="bright-blue"
                  loading={isVblLoading}
                />

                <StatsCard
                  title="Avg Degradation Time"
                  value={vblAnalysis ? `${vblAnalysis.summary.meanLife.toFixed(0)} days` : '—'}
                  description="Mean time from detection to failure"
                  color="green"
                  loading={isVblLoading}
                />

                <StatsCard
                  title="Selected for Analysis"
                  value={metadata?.totalSamples || 0}
                  description={`${metadata?.failureCount || 0} VBL records`}
                  color="purple"
                  loading={isVblLoading}
                />

                <StatsCard
                  title="Sites"
                  value={diversityMetrics?.uniqueSites || 0}
                  description="Wind farm locations"
                  color="fleet-monitor-blue"
                  loading={isVblLoading}
                />
              </ResponsiveGrid>

              {/* Grouped Parameter Cards */}
              {vblAnalysis && (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
                  {/* Weibull Parameters */}
                  <ParameterGroupCard
                    title="VBL Weibull Parameters"
                    variant="weibull"
                    helpText={`VBL Weibull Parameters for Degradation Analysis:

Scale (η): Characteristic degradation time at 63.2% probability of failure after detection`}
                    parameters={[
                      {
                        label: 'Shape Parameter (β)',
                        value: vblAnalysis.parameters.shape || 0,
                        ci: vblAnalysis.parameters.shapeCI ?
                          [vblAnalysis.parameters.shapeCI.lower, vblAnalysis.parameters.shapeCI.upper] : undefined,
                        skipInterpretation: true,
                      },
                      {
                        label: 'Shape SE',
                        value: vblAnalysis.statistics?.standardErrors?.shape ||
                          (vblAnalysis.parameters.shapeCI ?
                            (vblAnalysis.parameters.shapeCI.upper - vblAnalysis.parameters.shapeCI.lower) / (2 * 1.96) : 0),
                        skipInterpretation: true,
                      },
                      {
                        label: 'Scale Parameter (η)',
                        value: vblAnalysis.parameters.scale || 0,
                        unit: 'days',
                        ci: vblAnalysis.parameters.scaleCI ?
                          [vblAnalysis.parameters.scaleCI.lower, vblAnalysis.parameters.scaleCI.upper] : undefined,
                      },
                      {
                        label: 'Scale SE',
                        value: vblAnalysis.statistics?.standardErrors?.scale ||
                          (vblAnalysis.parameters.scaleCI ?
                            (vblAnalysis.parameters.scaleCI.upper - vblAnalysis.parameters.scaleCI.lower) / (2 * 1.96) : 0),
                        unit: 'days',
                        skipInterpretation: true,
                      },
                    ]}
                    compact={false}
                  />

                  {/* Degradation Characteristics */}
                  <ParameterGroupCard
                    title="Degradation Characteristics"
                    variant="life"
                    helpText={`Degradation metrics at different failure probabilities:

MTTF: Mean Time To Failure (from detection)
B10: Time at which 10% have failed
B50: Median degradation time (50% failures)
B63.2: Characteristic life (scale parameter)`}
                    parameters={[
                      {
                        label: 'Mean Degradation (MTTF)',
                        value: vblAnalysis.summary.meanLife || 0,
                        unit: 'days',
                        ci: (() => {
                          if (vblAnalysis.parameters.shapeCI && vblAnalysis.parameters.scaleCI) {
                            const gamma = (n: number) => {
                              const val = 1 + 1 / n;
                              if (val <= 1.5) return 0.886 * Math.pow(val, 0.865);
                              return Math.sqrt(2 * Math.PI / val) * Math.pow(val / Math.E, val);
                            };
                            const lower = vblAnalysis.parameters.scaleCI.lower * gamma(vblAnalysis.parameters.shapeCI.upper);
                            const upper = vblAnalysis.parameters.scaleCI.upper * gamma(vblAnalysis.parameters.shapeCI.lower);
                            return [lower, upper];
                          }
                          return undefined;
                        })(),
                        skipInterpretation: true,
                        description: 'Expected degradation time',
                      },
                      {
                        label: 'B10 Life',
                        value: vblAnalysis.parameters.scale * Math.pow(-Math.log(0.9), 1 / vblAnalysis.parameters.shape),
                        unit: 'days',
                        skipInterpretation: true,
                        description: '10% failure probability',
                      },
                      {
                        label: 'Median Life (B50)',
                        value: vblAnalysis.summary.medianLife ||
                          (vblAnalysis.parameters.scale * Math.pow(-Math.log(0.5), 1 / vblAnalysis.parameters.shape)),
                        unit: 'days',
                        ci: vblAnalysis.parameters.scaleCI ? [
                          vblAnalysis.parameters.scaleCI.lower * Math.pow(-Math.log(0.5), 1 / vblAnalysis.parameters.shape),
                          vblAnalysis.parameters.scaleCI.upper * Math.pow(-Math.log(0.5), 1 / vblAnalysis.parameters.shape)
                        ] : undefined,
                        skipInterpretation: true,
                        description: '50% failure probability',
                      },
                      {
                        label: 'Characteristic Life',
                        value: vblAnalysis.parameters.scale,
                        unit: 'days',
                        ci: vblAnalysis.parameters.scaleCI ?
                          [vblAnalysis.parameters.scaleCI.lower, vblAnalysis.parameters.scaleCI.upper] : undefined,
                        description: '63.2% failure probability',
                      },
                    ]}
                    compact={false}
                  />

                  {/* Model Fit */}
                  <ParameterGroupCard
                    title="Model Fit"
                    variant="fit"
                    helpText={`Model Quality Indicators:

R² > 0.95: Excellent fit
R² > 0.90: Good fit
R² > 0.80: Fair fit
R² < 0.80: Poor fit

Adjusted R²: Accounts for sample size
RMSE: Root Mean Square Error`}
                    parameters={[
                      {
                        label: 'Goodness of Fit (R²)',
                        value: vblAnalysis.parameters.rSquared || 0,
                      },
                      {
                        label: 'Adjusted R²',
                        value: (() => {
                          const r2 = vblAnalysis.parameters.rSquared || 0;
                          const n = vblAnalysis.summary.sampleSize || metadata?.totalSamples || 0;
                          const k = 2; // 2 parameters for Weibull
                          return n > k ? 1 - ((1 - r2) * (n - 1) / (n - k)) : r2;
                        })(),
                        skipInterpretation: true,
                      },
                      {
                        label: 'RMSE',
                        value: Math.sqrt(1 - (vblAnalysis.parameters.rSquared || 0)) * 0.5,
                        skipInterpretation: true,
                      },
                      {
                        label: 'Sample Size',
                        value: `${metadata?.totalSamples || vblAnalysis.summary.sampleSize || 0} points`,
                        skipInterpretation: true,
                      },
                      {
                        label: 'Method',
                        value: VBL_ANALYSIS_METHOD.toUpperCase(),
                        skipInterpretation: true,
                      },
                    ]}
                    compact={false}
                  />
                </div>
              )}

              {/* VBL Analysis - Single or Dual Plot based on viewport */}
              {showDualPlots ? (
                <DualVblPlotView
                  analysis={vblAnalysis}
                  isLoading={isVblLoading}
                  showConfidenceIntervals={showConfidenceIntervals}
                  confidenceLevel={confidenceLevel}
                  showCensoredData={showCensoredData}
                  censoredData={undefined}
                  colorBy={colorBy}
                  markerBy={markerBy}
                  sizeBy={sizeBy}
                  showLegend={showLegend}
                />
              ) : (
                <div className="mb-6">
                  <VblWeibullPanel
                    analysis={vblAnalysis}
                    isLoading={isVblLoading}
                    hasEnoughData={hasEnoughData}
                    sampleSize={sampleSize}
                    showConfidenceIntervals={showConfidenceIntervals}
                    confidenceLevel={confidenceLevel}
                    showCensoredData={showCensoredData}
                    metadata={metadata}
                    censoredData={undefined}
                    plotType={plotType}
                    showSettingsInSidebar={true}
                    colorBy={colorBy}
                    markerBy={markerBy}
                    sizeBy={sizeBy}
                    showLegend={showLegend}
                  />
                </div>
              )}

              {/* VBL Records Table */}
              <div className="mb-6">
                <TableCard
                  title="VBL Records"
                  headerActions={
                    <div className="flex items-center gap-4">
                      <button
                        onClick={handleSelectAll}
                        disabled={isLoadingVbl}
                        className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50"
                      >
                        {(() => {
                          const visibleCaseIds = Array.from(
                            new Set(paginatedVblRecords.map((f) => f.case_id.toString()))
                          );
                          const allVisibleExcluded = visibleCaseIds.every((id) =>
                            (filters.excludedCaseIds || []).includes(id)
                          );
                          return allVisibleExcluded ? 'Select All' : 'Deselect All';
                        })()}
                      </button>
                      <button
                        onClick={toggleAllRows}
                        disabled={isLoadingVbl}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                      >
                        {(() => {
                          const visibleIds = paginatedVblRecords.map((f) => f.failure_id);
                          const allVisibleExpanded = visibleIds.every((id) =>
                            expandedRows.has(id)
                          );
                          return allVisibleExpanded ? 'Collapse All' : 'Expand All';
                        })()}
                      </button>
                      <div className="text-sm text-gray-600">
                        {isLoadingVbl ? (
                          <WaveLoading size="sm" variant="dots" color="bright-blue" />
                        ) : (
                          `${paginatedVblRecords.length} shown • ${tablePaginationData.includedCount} included • ${completeVblRecords.length} total`
                        )}
                      </div>
                    </div>
                  }
                  footerActions={
                    !isLoadingVbl && tablePaginationData.totalPages > 1 ? (
                      <div className="flex items-center justify-between w-full">
                        <div className="flex-1 flex justify-between sm:hidden">
                          <button
                            onClick={() => handleTablePageChange(Math.max(1, currentTablePage - 1))}
                            disabled={currentTablePage === 1}
                            className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                          >
                            Previous
                          </button>
                          <button
                            onClick={() => handleTablePageChange(currentTablePage + 1)}
                            disabled={!tablePaginationData.hasMore}
                            className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                          >
                            Next
                          </button>
                        </div>
                        <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                          <div>
                            <p className="text-sm text-gray-700">
                              Showing{' '}
                              <span className="font-medium">
                                {(currentTablePage - 1) * tablePageSize + 1}
                              </span>{' '}
                              to{' '}
                              <span className="font-medium">
                                {Math.min(
                                  currentTablePage * tablePageSize,
                                  tablePaginationData.totalCount
                                )}
                              </span>{' '}
                              of <span className="font-medium">{tablePaginationData.totalCount}</span>{' '}
                              cases ({tablePaginationData.includedCount} included in analysis)
                            </p>
                          </div>
                          <div>
                            <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                              <button
                                onClick={() => handleTablePageChange(Math.max(1, currentTablePage - 1))}
                                disabled={currentTablePage === 1}
                                className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                              >
                                Previous
                              </button>
                              <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                                Page {currentTablePage} of {tablePaginationData.totalPages}
                              </span>
                              <button
                                onClick={() => handleTablePageChange(currentTablePage + 1)}
                                disabled={!tablePaginationData.hasMore}
                                className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                              >
                                Next
                              </button>
                            </nav>
                          </div>
                        </div>
                      </div>
                    ) : undefined
                  }
                  loading={isLoadingVbl}
                >
                  {!isLoadingVbl && (
                    <div className="table-scroll-wrapper">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-white border-b border-gray-100">
                          <tr>
                            <th className="px-6 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-12">
                              <input
                                type="checkbox"
                                checked={(() => {
                                  const visibleCaseIds = Array.from(
                                    new Set(paginatedVblRecords.map((f) => f.case_id.toString()))
                                  );
                                  const anyExcluded = visibleCaseIds.some((id: string) =>
                                    (filters.excludedCaseIds || []).includes(id)
                                  );
                                  return paginatedVblRecords.length > 0 && !anyExcluded;
                                })()}
                                onChange={handleSelectAllInPage}
                                className="rounded"
                              />
                            </th>

                            <ColumnHeader
                              title="Case ID"
                              sortKey="caseId"
                              sortDirection={sortConfig?.key === 'caseId' ? sortConfig.direction : null}
                              onSort={handleSort}
                              width="w-24"
                            />

                            <ColumnHeader
                              title="Turbine"
                              sortKey="turbine"
                              sortDirection={sortConfig?.key === 'turbine' ? sortConfig.direction : null}
                              onSort={handleSort}
                              filterType="multiselect"
                              filterOptions={apiFilterOptions?.turbineModels || []}
                              selectedValues={tableFilters.turbineModels}
                              onFilterChange={(values) =>
                                handleTableColumnFilter('turbineModels', values)
                              }
                              width="w-28"
                            />

                            <ColumnHeader
                              title="Site"
                              sortKey="site"
                              sortDirection={sortConfig?.key === 'site' ? sortConfig.direction : null}
                              onSort={handleSort}
                              filterType="multiselect"
                              filterOptions={apiFilterOptions?.sites || []}
                              selectedValues={tableFilters.sites}
                              onFilterChange={(values) => handleTableColumnFilter('sites', values)}
                              width="w-28"
                            />

                            <ColumnHeader
                              title="Component"
                              sortKey="componentMajor"
                              sortDirection={
                                sortConfig?.key === 'componentMajor' ? sortConfig.direction : null
                              }
                              onSort={handleSort}
                              filterType="multiselect"
                              filterOptions={apiFilterOptions?.componentsMajor || []}
                              selectedValues={tableFilters.componentsMajor}
                              onFilterChange={(values) => {
                                setTableFilters((prev) => ({
                                  ...prev,
                                  componentsMajor: values,
                                }));
                                setCurrentTablePage(1);
                              }}
                              width="w-28"
                            />

                            <ColumnHeader
                              title="Sub-component"
                              sortKey="componentMinor"
                              sortDirection={
                                sortConfig?.key === 'componentMinor' ? sortConfig.direction : null
                              }
                              onSort={handleSort}
                              filterType="multiselect"
                              filterOptions={apiFilterOptions?.componentsMinor || []}
                              selectedValues={tableFilters.componentsMinor}
                              onFilterChange={(values) => {
                                setTableFilters((prev) => ({
                                  ...prev,
                                  componentsMinor: values,
                                }));
                                setCurrentTablePage(1);
                              }}
                              width="w-28"
                            />

                            <ColumnHeader
                              title="Failure Mode"
                              sortKey="failureMode"
                              sortDirection={
                                sortConfig?.key === 'failureMode' ? sortConfig.direction : null
                              }
                              onSort={handleSort}
                              filterType="searchable"
                              filterOptions={apiFilterOptions?.failureModes || []}
                              selectedValues={tableFilters.failureModes}
                              onFilterChange={(values) => handleTableColumnFilter('failureModes', values)}
                              width="w-36"
                            />

                            <ColumnHeader
                              title="Type"
                              sortKey="type"
                              sortDirection={sortConfig?.key === 'type' ? sortConfig.direction : null}
                              onSort={handleSort}
                              filterType="multiselect"
                              filterOptions={apiFilterOptions?.outcomeName || []}
                              selectedValues={tableFilters.outcomeName}
                              onFilterChange={(values) => handleTableColumnFilter('outcomeName', values)}
                              width="w-24"
                            />

                            <ColumnHeader
                              title="Degradation Time (Days)"
                              sortKey="degradationTime"
                              sortDirection={
                                sortConfig?.key === 'degradationTime' ? sortConfig.direction : null
                              }
                              onSort={handleSort}
                              filterType="range"
                              rangeMin={tableFilters.degradationTimeMin}
                              rangeMax={tableFilters.degradationTimeMax}
                              onRangeChange={handleTableRangeFilter}
                              width="w-28"
                            />

                            <ColumnHeader
                              title="Detection Date"
                              sortKey="detectionDate"
                              sortDirection={
                                sortConfig?.key === 'detectionDate' ? sortConfig.direction : null
                              }
                              onSort={handleSort}
                              filterType="daterange"
                              dateFrom={tableFilters.dateFrom}
                              dateTo={tableFilters.dateTo}
                              onDateRangeChange={handleTableDateRangeFilter}
                              width="w-24"
                            />

                            <ColumnHeader
                              title="Down Date"
                              sortKey="downDate"
                              sortDirection={
                                sortConfig?.key === 'downDate' ? sortConfig.direction : null
                              }
                              onSort={handleSort}
                              width="w-24"
                            />

                            <th className="px-2 py-2 text-center w-8">
                              {/* Details column */}
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {paginatedVblRecords.map((vbl: VblRecord) => {
                            const isExcluded = (filters.excludedCaseIds || []).includes(
                              vbl.case_id.toString()
                            );
                            const isExpanded = expandedRows.has(vbl.failure_id);
                            return (
                              <React.Fragment key={vbl.failure_id}>
                                {/* Main compact row */}
                                <tr
                                  className={`hover:bg-gray-50 ${!isExcluded ? 'bg-blue-50' : ''} ${isExpanded ? 'border-b-0' : ''}`}
                                >
                                  <td className="px-6 py-4 whitespace-nowrap w-12">
                                    <input
                                      type="checkbox"
                                      checked={!isExcluded}
                                      onChange={() => handleSelectVblRecord(vbl.failure_id)}
                                      className="rounded"
                                    />
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900 w-24">
                                    {vbl.case_id ? (
                                      <a
                                        href={externalLinks.aiHub.caseManagement(vbl.case_id)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 hover:underline transition-colors"
                                        title={`View case ${vbl.case_id} in AI Hub`}
                                      >
                                        <span className="truncate max-w-[60px]">
                                          {vbl.case_id}
                                        </span>
                                      </a>
                                    ) : (
                                      <div className="truncate max-w-[80px] text-gray-400">
                                        N/A
                                      </div>
                                    )}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 w-28">
                                    <div className="truncate max-w-[96px]" title={vbl.turbine_name}>
                                      {vbl.turbine_name}
                                    </div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 w-28">
                                    <div className="truncate max-w-[96px]" title={vbl.site_name}>
                                      {vbl.site_name}
                                    </div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 w-28">
                                    <div className="truncate max-w-[96px]" title={vbl.major_component_name}>
                                      {vbl.major_component_name}
                                    </div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 w-28">
                                    <div className="truncate max-w-[96px]" title={vbl.component_name || ''}>
                                      {vbl.component_name || ''}
                                    </div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 w-36">
                                    <div className="truncate max-w-[144px]" title={vbl.failure_mode}>
                                      {vbl.failure_mode}
                                    </div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap w-24">
                                    <span
                                      className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${vbl.outcome_name.includes('Replace')
                                          ? 'bg-red-100 text-red-800'
                                          : vbl.outcome_name.includes('Planned')
                                            ? 'bg-blue-100 text-blue-800'
                                            : 'bg-yellow-100 text-yellow-800'
                                        }`}
                                    >
                                      <div className="truncate max-w-[60px]" title={vbl.outcome_name}>
                                        {vbl.outcome_name}
                                      </div>
                                    </span>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-mono w-28">
                                    {vbl.degradation_time}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 w-24">
                                    {new Date(vbl.detected_date).toLocaleDateString()}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 w-24">
                                    {vbl.down_date ? new Date(vbl.down_date).toLocaleDateString() : 'Active'}
                                  </td>
                                  <td className="px-2 py-4 whitespace-nowrap w-8 text-center">
                                    <button
                                      onClick={() => toggleRowExpansion(vbl.failure_id)}
                                      className="p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors duration-200"
                                      title={isExpanded ? 'Collapse details' : 'Expand details'}
                                    >
                                      <ChevronDownIcon
                                        className={`h-4 w-4 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''
                                          }`}
                                      />
                                    </button>
                                  </td>
                                </tr>

                                {/* Expanded details row */}
                                {isExpanded && (
                                  <tr
                                    className={`${!isExcluded ? 'bg-blue-50' : 'bg-gray-50'} border-b border-gray-200`}
                                  >
                                    <td colSpan={12} className="px-6 py-4">
                                      <div className="bg-white rounded-lg border border-gray-100 p-4 shadow-sm">
                                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                                          <div>
                                            <span className="font-medium text-gray-600">
                                              Full Turbine:
                                            </span>
                                            <div className="mt-1 text-gray-900">
                                              {vbl.turbine_name}
                                            </div>
                                          </div>
                                          <div>
                                            <span className="font-medium text-gray-600">
                                              Site Location:
                                            </span>
                                            <div className="mt-1 text-gray-900">{vbl.site_name}</div>
                                          </div>
                                          <div>
                                            <span className="font-medium text-gray-600">
                                              Component Details:
                                            </span>
                                            <div className="mt-1 text-gray-900">
                                              {vbl.major_component_name}
                                              {vbl.component_name && (
                                                <span className="text-gray-600">
                                                  {' '}
                                                  ({vbl.component_name})
                                                </span>
                                              )}
                                            </div>
                                          </div>
                                          <div>
                                            <span className="font-medium text-gray-600">
                                              Failure Mode:
                                            </span>
                                            <div className="mt-1 text-gray-900">
                                              {vbl.failure_mode}
                                            </div>
                                          </div>
                                          <div>
                                            <span className="font-medium text-gray-600">
                                              Outcome Type:
                                            </span>
                                            <div className="mt-1">
                                              <span
                                                className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${vbl.outcome_name.includes('Replace')
                                                    ? 'bg-red-100 text-red-800'
                                                    : vbl.outcome_name.includes('Planned')
                                                      ? 'bg-blue-100 text-blue-800'
                                                      : 'bg-yellow-100 text-yellow-800'
                                                  }`}
                                              >
                                                {vbl.outcome_name}
                                              </span>
                                            </div>
                                          </div>
                                          <div>
                                            <span className="font-medium text-gray-600">
                                              Degradation Details:
                                            </span>
                                            <div className="mt-1 text-gray-900">
                                              {vbl.degradation_time} days
                                              <span className="text-gray-600 ml-2">
                                                ({vbl.degradation_time_years.toFixed(2)} years)
                                              </span>
                                            </div>
                                          </div>
                                          <div>
                                            <span className="font-medium text-gray-600">
                                              Detection Date:
                                            </span>
                                            <div className="mt-1 text-gray-900">
                                              {new Date(vbl.detected_date).toLocaleDateString()}
                                            </div>
                                          </div>
                                          <div>
                                            <span className="font-medium text-gray-600">
                                              Down Date:
                                            </span>
                                            <div className="mt-1 text-gray-900">
                                              {vbl.down_date ? new Date(vbl.down_date).toLocaleDateString() : 'Still Active'}
                                            </div>
                                          </div>
                                          {vbl.actual_rts_date && (
                                            <div>
                                              <span className="font-medium text-gray-600">
                                                RTS Date:
                                              </span>
                                              <div className="mt-1 text-gray-900">
                                                {new Date(vbl.actual_rts_date).toLocaleDateString()}
                                              </div>
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    </td>
                                  </tr>
                                )}
                              </React.Fragment>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </TableCard>
              </div>
            </div>
          </div>
        </div>

        {/* Workspace Modals */}
        <SaveWorkspaceModal
          isOpen={showSaveModal}
          onClose={() => setShowSaveModal(false)}
          currentFilters={filters}
          currentAnalysisConfig={{
            showConfidenceIntervals,
            confidenceLevel,
            showCensoredData,
            method: VBL_ANALYSIS_METHOD,
          }}
          currentFailures={completeVblRecords}
          currentResults={enhancedVblAnalysis}
          onSuccess={handleSaveSuccess}
          workspaceType="vbl"
          workspaceContext={workspaceContext}
        />

        <LoadWorkspaceModal
          isOpen={showLoadModal}
          onClose={() => setShowLoadModal(false)}
          onLoadWorkspace={handleLoadWorkspace}
          workspaceType="vbl"
        />
      </div>
    </WorkspaceErrorBoundary>
  );
};

export default VblAnalysis;